/**
 * Types for the Milestone Visual Artifacts system.
 *
 * Source of truth: `docs/21_milestone_visual_artifacts.md`.
 *
 * An artifact is a permanent visual element that unlocks when the player
 * crosses a meaningful milestone (1st/10th/50th crystallization, 100/1000
 * taps, rhythm streaks, lessons, upgrades, etc.). Once unlocked it stays
 * visible forever — even through prestige — building a personal visible
 * signature of the player's path.
 */
import type { Epoch, GameState } from '@sai-clicker/game-core';

/** Where on screen the artifact attaches. Renderers are grouped per layer. */
export type ArtifactLayer =
  | 'spark' // inside / on the Spark body
  | 'spark-orbit' // rings / dots around the Spark
  | 'network' // background star network signatures
  | 'frame' // screen-edge ornament
  | 'counter' // around the awareness counter
  | 'epoch-badge' // near the epoch label
  | 'whisper'; // around the whisper ticker

/**
 * Pure-function trigger. Evaluator iterates the catalog and asks each
 * trigger to look at `state`. If it returns `true` AND the artifact is
 * not already in the unlocked set, it fires a reveal.
 */
export type ArtifactTrigger =
  | { readonly kind: 'crystallizationsAtLeast'; readonly value: number }
  | { readonly kind: 'crystallizationsExactly'; readonly value: number }
  | { readonly kind: 'totalTapsAtLeast'; readonly value: number }
  | { readonly kind: 'spiritBallsAtLeast'; readonly value: number }
  | { readonly kind: 'rhythmStreakReached'; readonly value: number }
  | { readonly kind: 'lessonsUnlockedAtLeast'; readonly value: number }
  | { readonly kind: 'phrasesCollectedAtLeast'; readonly value: number }
  | { readonly kind: 'daysPlayedAtLeast'; readonly value: number }
  | { readonly kind: 'activationsSeenAtLeast'; readonly value: number }
  | { readonly kind: 'goldenSparksCaughtAtLeast'; readonly value: number }
  | { readonly kind: 'profileNameSet' }
  | { readonly kind: 'epochReached'; readonly epoch: Epoch }
  | {
      readonly kind: 'firstTapInTimeWindow';
      readonly fromHour: number;
      readonly toHour: number;
    }
  /** JB 2026-05-24: каждый апгрейд = visible artifact. Doc 14 ties in here. */
  | {
      readonly kind: 'upgradePurchasedExactly';
      readonly upgradeId: string;
      readonly level: number;
    }
  | { readonly kind: 'totalUpgradesAtLeast'; readonly value: number };

/**
 * Reveal "weight" — how big a deal it is when this unlocks.
 *
 * `major` shows a full-screen activation-style overlay with backdrop
 * dimmer + Sai reveal text + DelayedButton — the player has to look.
 *
 * `minor` shows a corner toast for 5 seconds — non-blocking, the player
 * can ignore it and keep tapping. The artifact still appears
 * immediately in the layer it belongs to.
 */
export type RevealWeight = 'major' | 'minor';

export interface VisualArtifact {
  /** Stable id — never rename (it's persisted in localStorage). */
  readonly id: string;
  readonly trigger: ArtifactTrigger;
  readonly layer: ArtifactLayer;
  /** Short label shown in reveal and tooltip. */
  readonly displayName: string;
  /** Longer description for a future "memory gallery" screen. */
  readonly description?: string;
  /**
   * Static reveal line in Sai's voice — shown in the reveal overlay.
   * When `docs/19` AI agent ships, we may swap this for a per-player
   * generated line keyed on (artifact_id, profile). Static for now.
   */
  readonly revealLine: string;
  readonly weight: RevealWeight;
  /**
   * Lower = quieter when many artifacts share a layer. The renderer can
   * dim older / lower-priority artifacts when a new one appears on the
   * same layer. Defaults to 1.
   */
  readonly displayPriority?: number;
}

/**
 * What `evaluateArtifacts` returns — both the new unlocks for the reveal
 * queue and a flag for whether to fire `artifact.unlocked` telemetry.
 */
export interface ArtifactEvaluationResult {
  readonly newlyUnlocked: readonly VisualArtifact[];
}

/**
 * The narrow GameState fields the evaluator actually needs. Keeping it
 * narrow avoids accidental coupling — if a new trigger needs more
 * fields, widen this type intentionally.
 */
export interface ArtifactEvalState {
  readonly epoch: Epoch;
  readonly crystallizations: number;
  readonly totalTaps: number;
  readonly spiritBalls: number;
  readonly rhythmBest: number;
  readonly lessonsUnlocked: number;
  readonly phrasesCollected: number;
  readonly daysPlayed: number;
  readonly activationsSeen: number;
  readonly goldenSparksCaught: number;
  readonly hasProfileName: boolean;
  readonly hourOfDayLocal: number;
  /** Map of upgrade_id → current level (0 if never bought). */
  readonly upgradeLevels: ReadonlyMap<string, number>;
  readonly totalUpgrades: number;
}

/** Helper: extract the narrow state from full GameState + extras. */
export function extractEvalState(opts: {
  readonly state: GameState;
  /** How many Sai activations the player has dismissed so far. */
  readonly activationsSeen: number;
  /** Whether profile.displayName is set. */
  readonly hasProfileName: boolean;
  readonly now: Date;
  readonly upgradeLevels: ReadonlyMap<string, number>;
  readonly totalUpgrades: number;
  readonly phrasesCollected: number;
}): ArtifactEvalState {
  return {
    epoch: opts.state.epoch,
    crystallizations: opts.state.stats.totalCrystallizations,
    totalTaps: opts.state.stats.totalTaps,
    spiritBalls: opts.state.resources.spirit_balls,
    rhythmBest: opts.state.stats.tapsInRhythmBest,
    lessonsUnlocked: opts.state.lessonsUnlocked.size,
    phrasesCollected: opts.phrasesCollected,
    daysPlayed: estimateDaysPlayed(opts.state, opts.now.getTime()),
    activationsSeen: opts.activationsSeen,
    goldenSparksCaught: opts.state.stats.totalGoldenSparksCaught,
    hasProfileName: opts.hasProfileName,
    hourOfDayLocal: opts.now.getHours(),
    upgradeLevels: opts.upgradeLevels,
    totalUpgrades: opts.totalUpgrades,
  };
}

/**
 * Estimate days played from firstTapAt → now. Conservative: counts a
 * day as crossed when the wall-clock day changes, not just 24h elapsed.
 */
function estimateDaysPlayed(state: GameState, nowMs: number): number {
  const first = state.stats.firstTapAt;
  if (first === null) return 0;
  const firstDay = new Date(first);
  const today = new Date(nowMs);
  const msPerDay = 24 * 60 * 60 * 1000;
  // Calendar-day diff, not 24h chunks
  const dayDiff = Math.floor(
    (today.getTime() - firstDay.getTime()) / msPerDay +
      (firstDay.getHours() / 24 - today.getHours() / 24),
  );
  return Math.max(1, dayDiff + 1); // day 1 = first day
}
